import { useCallback, useEffect, useMemo, useState } from 'react';
import { commentStonesApi } from '@/services/commentStonesApi';
import {
  CommentStoneOption,
  mapCommentStonesToOptions,
} from '@/utils/commentStones';

interface UseTunnelStonesParams {
  backgroundMode: 'image' | 'tunnel';
}

export function useTunnelStones({ backgroundMode }: UseTunnelStonesParams) {
  const [tunnelStoneOptions, setTunnelStoneOptions] = useState<
    CommentStoneOption[]
  >([]);
  const [selectedStoneId, setSelectedStoneId] = useState<string>('');
  const [isLoadingTunnelStones, setIsLoadingTunnelStones] = useState(false);

  useEffect(() => {
    if (backgroundMode !== 'tunnel') {
      return;
    }

    let cancelled = false;

    const loadCommentStones = async () => {
      setIsLoadingTunnelStones(true);
      try {
        const response = await commentStonesApi.getCommentStones();
        if (cancelled || !response.success) {
          return;
        }

        const raw: any = response.data;
        const stones = Array.isArray(raw?.stones) ? raw.stones : [];
        const nextOptions = mapCommentStonesToOptions(stones);

        if (nextOptions.length > 0) {
          setTunnelStoneOptions(nextOptions);
        }
      } catch {
        // backend list is unavailable
      } finally {
        if (!cancelled) {
          setIsLoadingTunnelStones(false);
        }
      }
    };

    void loadCommentStones();

    return () => {
      cancelled = true;
    };
  }, [backgroundMode]);

  useEffect(() => {
    if (tunnelStoneOptions.length === 0) {
      return;
    }

    if (
      !selectedStoneId ||
      !tunnelStoneOptions.some(stone => stone.id === selectedStoneId)
    ) {
      setSelectedStoneId(tunnelStoneOptions[0].id);
    }
  }, [selectedStoneId, tunnelStoneOptions]);

  const selectedTunnelStone = useMemo(
    () =>
      tunnelStoneOptions.find(stone => stone.id === selectedStoneId) ??
      tunnelStoneOptions[0] ??
      null,
    [selectedStoneId, tunnelStoneOptions]
  );

  const tunnelStoneTextureUrls = useMemo(
    () => tunnelStoneOptions.map(stone => stone.imageUrl),
    [tunnelStoneOptions]
  );

  const handleTunnelStoneSelect = useCallback((stone: CommentStoneOption) => {
    setSelectedStoneId(stone.id);
  }, []);

  return {
    tunnelStoneOptions,
    selectedStoneId,
    selectedTunnelStone,
    tunnelStoneTextureUrls,
    isLoadingTunnelStones,
    handleTunnelStoneSelect,
  };
}
